import './Order.scss';

import cn from 'classnames';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as PdfIcon } from '@/common/assets/icons/pdf.svg';
import NavLinkExtended from '@/common/components/blocks/NavLinkExtended/NavLinkExtended';
import Price from '@/common/components/blocks/Price/Price';
import ButtonLoading from '@/common/components/molecules/ButtonLoading/ButtonLoading';
import { ORDER_STATUS, ROUTE_ORDERS, TYPE_ORIGIN } from '@/common/constants';
import { useAuth } from '@/common/contexts/AuthContext';
import { useConfig } from '@/common/contexts/ConfigContext';
import { useModalContext } from '@/common/contexts/ModalContext';
import useInvoice from '@/common/hooks/invoice/useInvoice';
import { ReactComponent as NotificationSvg } from '@/ebat/assets/icons/notif-order.svg';
import InvoicesAccess from '@/ebat/components/blocks/Modal/Order/InvoicesAccess/InvoicesAccess';
import ListAction from '@/ebat/components/blocks/Orders/ListAction/ListAction';

import { OrderDefaultProps, OrderPropTypes } from './Order.proptypes';

const Order = ({
  className,
  date,
  delivered,
  deliveryNotes,
  disabled,
  hasInvoice,
  id,
  isLoading,
  isShippingAvailable,
  number,
  origin,
  reference,
  shippingType,
  status,
  totalDelivered,
  totalLeft,
  totalRaw,
  unreadHistory,
  updated,
  worksite,
}) => {
  const { t, i18n } = useTranslation();
  const { user, zergRush } = useAuth();
  const { isEbat } = useConfig();
  const {
    onOpenModal,
    setModalClassName,
    setModalContent,
    setModalSize,
  } = useModalContext();

  const {
    onDownloadInvoice,
    onGetInvoices,
    isDisabled: isDisabledDownloadInvoice,
  } = useInvoice({ disabled, isLoading });

  const { code, label } = status;

  /**
   * Generate isDisabled object
   */
  const isDisabled = useMemo(() => {
    const loading = isLoading || isDisabledDownloadInvoice.loading;
    const blocked =
      loading ||
      disabled ||
      isDisabledDownloadInvoice.disabled ||
      !user?.functionality_accesses?.orders_order; // TODO: to check speck; // TODO: Zergrush ?

    return {
      blocked:
        blocked || status.code === ORDER_STATUS.BLOCKED || !isShippingAvailable,
      disabled: blocked,
      disabeldActions: blocked || (origin === TYPE_ORIGIN.EBC && isEbat),
      invoice: blocked || zergRush, // TODO: ACL zergRush to check
      loading,
    };
  }, [
    disabled,
    isDisabledDownloadInvoice,
    isEbat,
    isLoading,
    isShippingAvailable,
    origin,
    status,
    user,
    zergRush,
  ]);

  const handleOpenModal = useCallback(
    (invoices) => {
      setModalContent(
        <InvoicesAccess invoices={invoices.data} number={number} />,
      );
      setModalClassName('modal-invoices-access');
      setModalSize('sm');
      onOpenModal();
    },
    [number, onOpenModal, setModalClassName, setModalContent, setModalSize],
  );

  /**
   * If there is 1 invoice it is downloaded directly, if there is more it is opened in the modal
   * @param {object} invoices with data invoices
   */
  const onInvoicesAccess = useCallback(
    (invoices) => {
      if (invoices.data.length === 1) {
        onDownloadInvoice({
          order: { number },
          invoice: { id: invoices.data[0].id },
        });
      } else {
        handleOpenModal(invoices);
      }
    },
    [number, handleOpenModal, onDownloadInvoice],
  );

  const handleClick = useCallback(
    (e) => {
      const element = e.target;

      if (
        isDisabled.disabled ||
        element.closest('.order__data--invoice') ||
        element.closest('.order__data--actions')
      ) {
        e.preventDefault();
      }
    },
    [isDisabled],
  );

  return (
    <NavLinkExtended
      className={cn('order', className, {
        'order--updated': updated,
        'order--disabled': isDisabled.disabled,
      })}
      context="order-item"
      data-qa-order-id={id}
      disabled={isDisabled.disabled}
      id={`order-${id}`}
      onClick={handleClick}
      to={`${ROUTE_ORDERS}/${id}`}
    >
      {unreadHistory > 0 && (
        <NotificationSvg className={cn('order__notifications-icon')} />
      )}

      <span
        className="order__data order__data--date"
        data-qa-id="order-info-date"
      >
        {i18n.format(date, 'short')}
      </span>
      <span
        className="order__data order__data--number"
        data-qa-id="order-info-number"
      >
        <span className="number">{t('ebat:entities:orders.number')}</span>
        {number}
      </span>
      <span
        className="order__data order__data--reference"
        data-qa-id="order-info-reference"
      >
        {reference}
      </span>
      <span
        className={cn(
          'order__data',
          'order__data--state',
          `order__data--${code}`,
        )}
        data-qa-id="order-info-state"
      >
        {label}
      </span>
      <span
        className="order__data order__data--worksite"
        data-qa-id="order-info-worksite"
      >
        {worksite}
      </span>
      <span
        className="order__data order__data--remains"
        data-qa-id="order-info-remains"
      >
        <span className="label">
          {t('ebat:entities:orders.label.partial_shipping')}
        </span>
        {totalLeft ? (
          <Price
            type={t('common:messages:price.types.ht')}
            typeSup
            value={totalRaw - totalDelivered}
          />
        ) : (
          '--'
        )}
      </span>
      <span
        className="order__data order__data--amount"
        data-qa-id="order-info-amount"
      >
        <Price
          type={t('common:messages:price.types.ht')}
          typeSup
          value={totalRaw}
        />
      </span>
      <span
        className="order__data order__data--origin"
        data-qa-id="order-info-origin"
      >
        {t(`ebat:messages:origin.${origin}`)}
      </span>
      <span
        className="order__data order__data--invoice"
        data-qa-id="order-info-invoice"
      >
        {hasInvoice && (
          <ButtonLoading
            className="button--document"
            context="pdf-save"
            disabled={isDisabled.invoice}
            isLoading={isDisabled.loading}
            onClick={() =>
              onGetInvoices({
                orderNumber: number,
                onSuccess: onInvoicesAccess,
              })
            }
            variant="primary-light"
          >
            <PdfIcon className="button__icon" />
          </ButtonLoading>
        )}
      </span>
      <div
        className="order__data order__data--actions"
        data-qa-id="order-info-actions"
      >
        {!isDisabled.blocked && (
          <ListAction
            delivered={delivered}
            deliveryNotes={deliveryNotes}
            disabled={isDisabled.disabeldActions}
            id={id}
            isLoading={isDisabled.loading}
            isShippingAvailable={isShippingAvailable}
            origin={origin}
            reference={reference}
            shippingType={shippingType}
            status={status}
          />
        )}
      </div>
    </NavLinkExtended>
  );
};

Order.defaultProps = {
  ...OrderDefaultProps,
};

Order.propTypes = {
  ...OrderPropTypes,
};

export default Order;
